import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Flex,
  Select,
  Heading,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Button,
  HStack,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useDashboard } from './useDashboard';
import { useDepartments } from '../../EOS/Departments/useDepartments';
import { DashboardStatsCards } from './components/DashboardStatsCards';
import { DashboardViewToggle } from './components/DashboardViewToggle';
import { ViewModeToggle } from './components/ViewModeToggle';
import { CollaboratorToggle } from './components/CollaboratorToggle';
import { RockCard } from './components/RockCard';
import { RocksTableView } from './components/RocksTableView';
import { CompanyIssuesList } from './components/CompanyIssuesList';
import { calculateQuarter, calculateWeek } from './dashboard.util';
import type { ViewMode, DashboardView, RockWithUsers, RockWithDetails } from './dashboard.types';
import type { Milestone, ActionItem } from '../../EOS/types';

/**
 * Dashboard Page - Main EOS dashboard with statistics and quick access
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 * Full implementation with dual views (Rocks/Company Issues) and dual modes (Cards/Table)
 */
export function DashboardPage() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const [dashboardView, setDashboardView] = useState<DashboardView>('rocks');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  // Initialize with 'all' - user can change to their own user if desired
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [showCollaboratorRocks, setShowCollaboratorRocks] = useState<boolean>(false);
  
  // Track if user has manually changed the filter to prevent auto-resets
  const userManuallyChangedFilter = useRef(false);
  
  // Stable handler for user filter changes
  const handleUserFilterChange = useCallback((newValue: string) => {
    userManuallyChangedFilter.current = true;
    setSelectedUserFilter(newValue);
    // If user selects "all", disable collaborator toggle
    if (newValue === 'all') {
      setShowCollaboratorRocks(false);
    }
  }, []);

  const {
    rocks,
    issues,
    users,
    milestones: allMilestones,
    actionItems: allActionItems,
    companyIssues,
    loading,
    getFilteredRocks,
    getFilteredCompanyIssues,
    getStats,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
    getOverdueMilestones,
    getOverdueActionItems,
    subscribeToIssues,
  } = useDashboard(currentUser?.uid);

  // State for real-time issues updates
  const [tableIssues, setTableIssues] = useState(issues);

  const { departments } = useDepartments();


  // Enrich rocks with user objects
  const rocksWithUsers = useMemo<RockWithUsers[]>(() => {
    if (rocks.length === 0 || users.length === 0) return [];
    const usersMap = new Map(users.map((u) => [u.id, u]));
    return rocks.map((rock) => ({
      ...rock,
      owner: usersMap.get(rock.ownerId),
      collaboratorUsers:
        rock.collaborators
          ?.map((id) => usersMap.get(id))
          .filter((u): u is typeof users[0] => u !== undefined) || [],
    }));
  }, [rocks, users]);

  // State for view-specific data
  const [milestonesForCards, setMilestonesForCards] = useState<Map<string, Milestone[]>>(new Map());
  const [rocksWithDetails, setRocksWithDetails] = useState<RockWithDetails[]>([]);
  const [userMilestones, setUserMilestones] = useState<Milestone[]>([]);
  const [userActionItems, setUserActionItems] = useState<ActionItem[]>([]);

  // Load milestones for card view
  useEffect(() => {
    if (viewMode === 'cards' && rocks.length > 0) {
      const rockIds = rocks.map((r) => r.id);
      loadMilestonesForRocks(rockIds).then((milestonesMap) => {
        setMilestonesForCards(milestonesMap);
      });
    }
  }, [viewMode, rocks, loadMilestonesForRocks]);

  // Load table data (milestones, action items) and set up real-time subscription
  useEffect(() => {
    if (viewMode === 'table' && rocksWithUsers.length > 0) {
      const rockIds = rocksWithUsers.map((r) => r.id);
      
      // Load milestones and action items
      Promise.all([
        loadMilestonesForRocks(rockIds),
        loadActionItemsForRocks(rockIds),
      ]).then(([milestonesMap, actionItemsMap]) => {
        const enriched = rocksWithUsers.map((rock) => ({
          ...rock,
          milestones: milestonesMap.get(rock.id) || [],
          actionItems: actionItemsMap.get(rock.id) || [],
        }));
        setRocksWithDetails(enriched);
      });

      // Set up real-time subscription for issues
      const unsubscribe = subscribeToIssues(rockIds, (updatedIssues) => {
        setTableIssues(updatedIssues);
        // Reload table data when issues change
        Promise.all([
          loadMilestonesForRocks(rockIds),
          loadActionItemsForRocks(rockIds),
        ]).then(([milestonesMap, actionItemsMap]) => {
          const enriched = rocksWithUsers.map((rock) => ({
            ...rock,
            milestones: milestonesMap.get(rock.id) || [],
            actionItems: actionItemsMap.get(rock.id) || [],
          }));
          setRocksWithDetails(enriched);
        });
      });

      return () => {
        unsubscribe();
      };
    } else {
      // Reset issues when not in table view
      setTableIssues(issues);
    }
  }, [
    viewMode,
    rocksWithUsers,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
    subscribeToIssues,
    issues,
  ]);

  // Load user-specific milestones and todos for statistics
  useEffect(() => {
    if (selectedUserFilter && selectedUserFilter !== 'all' && rocksWithUsers.length > 0) {
      const filteredRocks = getFilteredRocks(
        selectedUserFilter,
        selectedDepartment,
        showCollaboratorRocks,
        currentUser?.uid
      );
      const userRockIds = filteredRocks.map((r) => r.id);
      if (userRockIds.length > 0) {
        Promise.all([
          loadMilestonesForRocks(userRockIds),
          loadActionItemsForRocks(userRockIds),
        ]).then(([milestonesMap, actionItemsMap]) => {
          setUserMilestones(Array.from(milestonesMap.values()).flat());
          setUserActionItems(Array.from(actionItemsMap.values()).flat());
        });
      } else {
        setUserMilestones([]);
        setUserActionItems([]);
      }
    } else {
      setUserMilestones([]);
      setUserActionItems([]);
    }
  }, [
    selectedUserFilter,
    selectedDepartment,
    showCollaboratorRocks,
    rocksWithUsers,
    currentUser?.uid,
    getFilteredRocks,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
  ]);

  // Filter rocks based on current filters
  const filteredRocksWithUsers = useMemo(() => {
    return getFilteredRocks(
      selectedUserFilter,
      selectedDepartment,
      showCollaboratorRocks,
      currentUser?.uid
    ).map((rock) => {
      const enriched = rocksWithUsers.find((r) => r.id === rock.id);
      return enriched || rock;
    });
  }, [
    rocksWithUsers,
    selectedUserFilter,
    selectedDepartment,
    showCollaboratorRocks,
    currentUser?.uid,
    getFilteredRocks,
  ]);

  // Filter rocks with details for table view
  const filteredRocksWithDetails = useMemo(() => {
    return rocksWithDetails.filter((rock) => {
      if (selectedDepartment !== 'all' && rock.departmentId !== selectedDepartment) {
        return false;
      }
      if (selectedUserFilter === 'all') return true;
      if (showCollaboratorRocks && selectedUserFilter === currentUser?.uid) {
        return rock.collaborators?.includes(selectedUserFilter);
      }
      return rock.ownerId === selectedUserFilter;
    });
  }, [
    rocksWithDetails,
    selectedDepartment,
    selectedUserFilter,
    showCollaboratorRocks,
    currentUser?.uid,
  ]);

  // Filter company issues
  const filteredCompanyIssues = useMemo(() => {
    return getFilteredCompanyIssues(selectedUserFilter, selectedDepartment);
  }, [companyIssues, selectedUserFilter, selectedDepartment, getFilteredCompanyIssues]);

  // Calculate statistics
  const stats = useMemo(() => {
    return getStats(
      selectedUserFilter,
      selectedDepartment,
      showCollaboratorRocks,
      currentUser?.uid,
      userMilestones,
      userActionItems
    );
  }, [
    getStats,
    selectedUserFilter,
    selectedDepartment,
    showCollaboratorRocks,
    currentUser?.uid,
    userMilestones,
    userActionItems,
  ]);

  // Calculate quarter and week
  const quarter = calculateQuarter();
  const week = calculateWeek();
  const currentYear = new Date().getFullYear();

  // Navigation handlers
  const handleMilestonesClick = () => {
    const overdue = getOverdueMilestones(userMilestones);
    if (overdue.length > 0) {
      const firstOverdue = overdue.sort(
        (a, b) =>
          new Date(a.dueDate as Date).getTime() - new Date(b.dueDate as Date).getTime()
      )[0];
      if (firstOverdue.rockId) {
        history.push(`/eos/rocks#rocks/${firstOverdue.rockId}`);
      }
    }
  };

  const handleActionItemsClick = () => {
    const overdue = getOverdueActionItems(userActionItems);
    if (overdue.length > 0) {
      const firstOverdue = overdue.sort(
        (a, b) =>
          new Date(a.dueDate as Date).getTime() - new Date(b.dueDate as Date).getTime()
      )[0];
      if (firstOverdue.linkedRockId) {
        history.push(`/eos/rocks#rocks/${firstOverdue.linkedRockId}`);
      }
    }
  };

  const handleCompanyIssuesClick = () => {
    history.push('/eos/issues?filter=company-issues');
  };

  const handleRockClick = (rockId: string) => {
    history.push(`/eos/rocks#rocks/${rockId}`);
  };

  const handleIssueClick = (issueId: string) => {
    history.push(`/eos/issues?selected=${issueId}`);
  };

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.700', 'gray.300');
  const selectBg = useColorModeValue('white', 'gray.700');
  const emptyStateBg = useColorModeValue('white', 'gray.700');
  const emptyStateBorder = useColorModeValue('gray.200', 'gray.600');
  const emptyStateText = useColorModeValue('gray.500', 'gray.400');
  const emptyStateSubtext = useColorModeValue('gray.400', 'gray.500');
  const yellowBg = useColorModeValue('yellow.50', 'yellow.900');
  const yellowBorder = useColorModeValue('yellow.200', 'yellow.700');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading dashboard...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}             
>
      <VStack spacing={4} align="stretch">
          {/* Header Section */}
          <Box>
            <Heading size="md" color={headingColor} mb={2}>
              {quarter} {currentYear} Dashboard
            </Heading>
            <Text color={subheadingColor}>
              Week {week.current} of {week.total} - Track quarterly priorities and accountability
            </Text>
          </Box>

          {/* Filters and Controls */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={4} flexWrap="wrap">
            {/* User Filter */}
            <HStack spacing={2}>
              <Text color={filterLabelColor} fontWeight="medium" whiteSpace="nowrap">
                Filter by User:
              </Text>
              <Select
                value={selectedUserFilter}
                onChange={(e) => handleUserFilterChange(e.target.value)}
                bg={selectBg}
                maxW="300px"
              >
                <option value="all">All Users</option>
                {users
                  .filter((user) => user.isEOSUser)
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
              </Select>
            </HStack>

            {/* Department Filter */}
            <HStack spacing={2}>
              <Text color={filterLabelColor} fontWeight="medium" whiteSpace="nowrap">
                Department:
              </Text>
              <Select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                bg={selectBg}
                maxW="300px"
              >
                <option value="all">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Rocks View Only Controls */}
            {dashboardView === 'rocks' && (
              <>
                <ViewModeToggle value={viewMode} onChange={setViewMode} />
                <CollaboratorToggle
                  value={showCollaboratorRocks}
                  onChange={(value) => {
                    // Only allow changing collaborator toggle if filter is NOT 'all'
                    // This prevents any interference when user selects "All Users"
                    if (selectedUserFilter === 'all') {
                      return; // Don't allow collaborator toggle when "all" is selected
                    }
                    setShowCollaboratorRocks(value);
                    // Only auto-set filter when collaborator toggle is enabled
                    // AND user hasn't manually set it to 'all'
                    // AND current filter is not already the current user
                    if (value && 
                        !userManuallyChangedFilter.current && 
                        selectedUserFilter !== currentUser?.uid) {
                      setSelectedUserFilter(currentUser?.uid || 'all');
                    }
                  }}
                  disabled={selectedUserFilter !== currentUser?.uid || selectedUserFilter === 'all'}
                />
              </>
            )}
          </HStack>

          {/* Right Side Controls */}
          <HStack spacing={3} flexWrap="wrap">
            <DashboardViewToggle value={dashboardView} onChange={setDashboardView} />

            {/* Company Issues View Actions */}
            {dashboardView === 'company-issues' && (
              <>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => history.push('/eos/issues?filter=company-issues')}
                >
                  View All Issues
                </Button>
                <HStack
                  px={3}
                  py={2}
                  bg={yellowBg}
                  rounded="lg"
                  borderWidth="1px"
                  borderColor={yellowBorder}
                >
                  <Icon as={AlertTriangle} w={4} h={4} color="yellow.600" />
                  <Heading size="lg" color={headingColor}>
                    {stats.companyIssues.total}
                  </Heading>
                </HStack>
              </>
            )}
          </HStack>
        </Flex>

        {/* Statistics Dashboard (Rocks view only) */}
        {dashboardView === 'rocks' && (
          <Box>
            <DashboardStatsCards
              stats={stats}
              onMilestonesClick={handleMilestonesClick}
              onActionItemsClick={handleActionItemsClick}
              onCompanyIssuesClick={handleCompanyIssuesClick}
            />
          </Box>
        )}

        {/* Content Area */}
        {dashboardView === 'company-issues' ? (
          <CompanyIssuesList
            issues={filteredCompanyIssues}
            users={users}
            departments={departments}
            onIssueClick={handleIssueClick}
            onViewAllClick={handleCompanyIssuesClick}
          />
        ) : filteredRocksWithUsers.length === 0 && viewMode === 'cards' ? (
          <Box textAlign="center" py={12} bg={emptyStateBg} rounded="lg" borderWidth="1px" borderColor={emptyStateBorder}>
            <Text color={emptyStateText} fontSize="lg">
              No rocks found
            </Text>
            <Text color={emptyStateSubtext} fontSize="sm" mt={1}>
              {rocksWithUsers.length === 0
                ? 'Go to All Rocks to create your first 90-day goal'
                : 'No rocks match the selected filter'}
            </Text>
            <Button
              mt={4}
              colorScheme="blue"
              onClick={() => history.push('/eos/rocks')}
            >
              Go to All Rocks
            </Button>
          </Box>
        ) : viewMode === 'cards' ? (
          <Grid 
            templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} 
            gap={4} 
            autoRows="1fr"
            w="100%"
            maxW="100%"
          >
            {filteredRocksWithUsers.map((rock) => {
              const milestones = milestonesForCards.get(rock.id) || [];
              return (
                <GridItem 
                  key={rock.id} 
                  display="flex" 
                  w="100%" 
                  minW={0}
                  maxW="100%"
                  overflow="hidden"
                >
                  <RockCard
                    rock={rock}
                    milestones={milestones}
                    onClick={() => handleRockClick(rock.id)}
                  />
                </GridItem>
              );
            })}
          </Grid>
        ) : (
          <RocksTableView
            rocks={filteredRocksWithDetails}
            issues={viewMode === 'table' ? tableIssues : issues}
            users={users}
            onRockClick={handleRockClick}
            onIssueClick={handleRockClick}
          />
        )}
      </VStack>
    </Container>
  );
}
